import {
  CalendarRangeIcon,
  CheckCircleIcon,
  ForumIcon,
  MultipleStopRoundedIcon,
  TagIcon,
} from '@atgfr8/icons';
import type { CollectionFlagTypeName } from '@store/collectionFlags/types';
import { DateTime } from 'luxon';

export const COLLECTION_FLAG_MODAL_ID = 'collectionFlagModalId';

export const COLLECTION_FLAG_OPTION_PREFIX = 'collection:';

type CollectionFlagBannerStyle = {
  containerClass: string;
  titleClass: string;
  noteClass: string;
  iconClass: string;
  icon: typeof CheckCircleIcon;
};

type CollectionFlagTypeMeta = {
  id: number;
  value: CollectionFlagTypeName;
  label: string;
  description: string;
  banner: CollectionFlagBannerStyle;
};

export const COLLECTION_FLAG_TYPES: CollectionFlagTypeMeta[] = [
  {
    id: 6,
    value: 'ContactAgentOnly',
    label: 'Contact-Agent Only',
    description: 'Contact Agent Only',
    banner: {
      containerClass: 'border-[#512DA8] bg-[#E8EAF6]',
      titleClass: 'text-[#311B92]',
      noteClass: 'text-[#311B92]',
      iconClass: 'text-[#4527A0]',
      icon: CheckCircleIcon,
    },
  },
  {
    id: 7,
    value: 'PendingThirdPartyCollections',
    label: 'Pending 3rd Party Collections',
    description: 'Pending 3rd Party Collections',
    banner: {
      containerClass: 'border-[#512DA8] bg-[#EDE7F6]',
      titleClass: 'text-[#512DA8]',
      noteClass: 'text-[#512DA8]',
      iconClass: 'text-[#512DA8]',
      icon: CalendarRangeIcon,
    },
  },
  {
    id: 8,
    value: 'InThirdPartyCollections',
    label: 'In 3rd Party Collections',
    description: 'In 3rd Party Collections',
    banner: {
      containerClass: 'border-[#FF9800] bg-[#FCE2BE]',
      titleClass: 'text-[#4527A0]',
      noteClass: 'text-[#4527A0]',
      iconClass: 'text-[#4527A0]',
      icon: MultipleStopRoundedIcon,
    },
  },
  {
    id: 9,
    value: 'SpanishSpeaking',
    label: 'Spanish Speaking',
    description: 'Spanish Speaking',
    banner: {
      containerClass: 'border-[#AFB42B] bg-[#F9FBE7]',
      titleClass: 'text-[#512DA8]',
      noteClass: 'text-[#512DA8]',
      iconClass: 'text-[#512DA8]',
      icon: ForumIcon,
    },
  },
  {
    id: 10,
    value: 'Other',
    label: 'Other',
    description: 'Other',
    banner: {
      containerClass: 'border-[#000000] bg-[#ECEFF1]',
      titleClass: 'text-[#212121]',
      noteClass: 'text-[#263238]',
      iconClass: 'text-[#212121]',
      icon: TagIcon,
    },
  },
];

const OTHER_FLAG_TYPE = COLLECTION_FLAG_TYPES[COLLECTION_FLAG_TYPES.length - 1];

export const COLLECTION_FLAG_TYPE_OPTIONS = COLLECTION_FLAG_TYPES.map(
  (type) => ({ label: type.label, value: type.value }),
);

export const findCollectionFlagType = (
  value: string | number | null | undefined,
): CollectionFlagTypeMeta | undefined => {
  if (value === null || value === undefined) return undefined;
  const normalized = String(value).trim().toLowerCase();
  return COLLECTION_FLAG_TYPES.find(
    (type) =>
      String(type.id) === normalized ||
      type.value.toLowerCase() === normalized ||
      type.label.toLowerCase() === normalized ||
      type.description.toLowerCase() === normalized,
  );
};

export const getCollectionFlagLabel = (value: string | number) =>
  findCollectionFlagType(value)?.label ?? String(value);

export const getCollectionFlagBannerStyle = (
  value: string | number,
): CollectionFlagBannerStyle =>
  findCollectionFlagType(value)?.banner ?? OTHER_FLAG_TYPE.banner;

export const toCollectionFlagOptionValue = (value: CollectionFlagTypeName) =>
  `${COLLECTION_FLAG_OPTION_PREFIX}${value}`;

export const parseCollectionFlagOptionValue = (
  optionValue: string | null | undefined,
): CollectionFlagTypeName | undefined => {
  if (optionValue?.startsWith(COLLECTION_FLAG_OPTION_PREFIX) !== true) {
    return undefined;
  }
  return findCollectionFlagType(
    optionValue.slice(COLLECTION_FLAG_OPTION_PREFIX.length),
  )?.value;
};

export const PREFIXED_COLLECTION_FLAG_TYPE_OPTIONS = COLLECTION_FLAG_TYPES.map(
  (type) => ({
    label: type.label,
    value: toCollectionFlagOptionValue(type.value),
  }),
);

export const formatCollectionFlagDate = (
  flagDate: string | null | undefined,
) => {
  if (flagDate === null || flagDate === undefined || flagDate === '') return '';
  const parsed = DateTime.fromISO(flagDate, { zone: 'utc' });
  return parsed.isValid ? parsed.toLocal().toFormat('MM/dd/yyyy  h:mm a') : '';
};
